import React, { useMemo } from 'react';
import { Keyboard } from 'react-native';
import Toast from 'react-native-root-toast';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { PRIORITY } from '../../../common/constants';
import { CREATE_TICKET } from '../../../common/graphql/ticket';
import { getFullMachineName } from '../../../common/utils/machine';
import { getCreatedTicketVisibleToDriversDefault } from '../../../common/utils/ticket/areTicketQueuesEnabled';
import { useUserContext } from '../../../common/utils/context';
import { Screen } from '../../design/components';
import useBackLabel from '../../hooks/useBackLabel';
import { useOfflineQueue } from '../../offline';
import TicketCreator from './TicketCreator';
import TrainingModeBanner from '../myDay/TrainingModeBanner';
import { isMyDayTraining } from '../myDay/myDayTraining';

const GET_DOCKET_MACHINE_FOR_TICKET = gql`
	query ($id: String) {
		getServiceDocket(id: $id) {
			id
			machine {
				id
				assetId
				place {
					id
					name
				}
				location {
					id
					name
				}
			}
		}
	}
`;

const TicketCreatorView = () => {
	const navigation = useNavigation();
	const route = useRoute();
	const backLabel = useBackLabel();
	const { tenantSettings } = useUserContext();
	const { isOnline } = useOfflineQueue();
	const trainingContext = route.params?.myDayTraining;
	const isTraining = isMyDayTraining(trainingContext);
	const visibleToDriversByDefault = getCreatedTicketVisibleToDriversDefault(tenantSettings);

	const machineId = route.params?.machineId;
	const machineName = route.params?.machineName;
	const docketId = route.params?.docketId;

	// Re-fetch the docket machine by id so we don't rely on possibly stale navigation params
	const { data: docketData } = useQuery(GET_DOCKET_MACHINE_FOR_TICKET, {
		variables: { id: docketId },
		skip: !docketId,
		fetchPolicy: isTraining ? 'no-cache' : 'cache-first',
	});

	const docketMachine = docketData?.getServiceDocket?.machine;
	const resolvedMachineId = docketMachine?.id || machineId;
	const resolvedMachineName = useMemo(() => {
		if (docketMachine) {
			return getFullMachineName(docketMachine, docketMachine.place, docketMachine.location);
		}
		if (machineName) {
			return machineName;
		}
		return undefined;
	}, [machineName, docketMachine]);

	const initialValues = useMemo(() => {
		const defaults = {
			visibleToDrivers: visibleToDriversByDefault,
			tags: [],
			machines: [],
			priority: PRIORITY.NORMAL,
			docketId,
			...(isTraining ? { adminTraining: true } : {}),
		};
		if (resolvedMachineId) {
			defaults.machine = resolvedMachineId;
		}
		return defaults;
	}, [docketId, isTraining, resolvedMachineId, visibleToDriversByDefault]);

	const ticketFormKey = `${resolvedMachineId || 'no-machine'}-${docketId || 'no-docket'}-${visibleToDriversByDefault ? 'driver-visible' : 'triage'}`;

	return (
		<Screen
			headerRight={<ServiceNoteButton />}
			heading="Create ticket"
			backLabel={backLabel}
			subheading={resolvedMachineName}
			banner={<TrainingModeBanner trainingContext={trainingContext} isOnline={isOnline} />}
		>
			<TicketCreator
				key={ticketFormKey}
				offlineConfigKey="createTicket"
				disableOffline={isTraining}
				disableActions={isTraining && !isOnline}
				onSubmitSuccess={(ticket) => {
					Keyboard.dismiss();
					navigation.goBack();
					Toast.show(ticket?.offline ? 'Ticket queued; will sync when back online' : 'Ticket successfully created');
				}}
				createMutation={CREATE_TICKET}
				initialValues={initialValues}
				docketId={docketId}
				machineId={resolvedMachineId}
			/>
		</Screen>
	);
};

export default TicketCreatorView;
